import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CARD_MOTION = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = apiError(err, "Unable to sign in");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-grid flex items-center justify-center px-5 py-12">
      <motion.div
        {...CARD_MOTION}
        className="w-full max-w-md"
      >
        <div className="flex flex-col items-center mb-8">
          <Logo className="h-10 mb-6" />
          <div className="text-[11px] uppercase tracking-[0.3em] text-brand-blue font-semibold">
            Presentation Portal
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-card/80 backdrop-blur-xl p-8 shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
          <h1 className="font-heading text-2xl font-semibold text-white tracking-tight">
            Admin sign in
          </h1>
          <p className="text-sm text-muted-foreground mt-1 mb-7">
            Sign in to manage your corporate presentations.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="username"
                required
                placeholder="admin@entrolabs.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-background/60 border-white/10 h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-background/60 border-white/10 h-11"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-full bg-brand-blue hover:bg-brand-bluehover text-white font-medium transition-all hover:-translate-y-0.5"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Lock className="h-4 w-4" /> Sign in
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          EntroLabs IT Solutions · Secure internal portal
        </p>
      </motion.div>
    </div>
  );
}
